import { DEG, clamp01 } from '../../core/math';
import { resolvePalette, type ResolvedPalette } from '../../core/palette';
import type { RenderTarget, TechniqueContext } from '../types';
import type { AsciiMosaicValues } from './params';

/**
 * Rebuilds the text out of smaller characters.
 *
 * Each cell samples the field and picks a character by density. Coverage is averaged
 * over a few sub-samples rather than read at the cell centre — a single sample makes
 * thin strokes flicker between "empty" and "full" depending on where the grid happens
 * to land, which reads as noise rather than as type.
 */
export interface AsciiCell {
  x: number;
  y: number;
  char: string;
  density: number;
  distance: number;
  size: number;
  rotation: number;
  tint: number;
  isBackground: boolean;
}

export interface AsciiMosaicState {
  cells: AsciiCell[];
  palette: ResolvedPalette;
  built: boolean;
}

function values(ctx: TechniqueContext): AsciiMosaicValues {
  return ctx.values as unknown as AsciiMosaicValues;
}

export function init(ctx: TechniqueContext): AsciiMosaicState {
  const v = values(ctx);
  return { cells: [], palette: resolvePalette(v.palette, ctx.rng.fork(1)), built: false };
}

export function step(state: AsciiMosaicState, _dt: number, ctx: TechniqueContext): AsciiMosaicState {
  if (state.built) return state;
  const v = values(ctx);

  const cellW = Math.max(1, v.cell);
  const cellH = Math.max(1, v.cell * v.aspect);
  const chars = [...(v.charset || ' .:-=+*#%@')];
  const bgChars = [...(v.backgroundCharset || '·')];
  if (chars.length === 0) {
    state.built = true;
    return state;
  }

  const cols = Math.ceil(ctx.width / cellW);
  const rows = Math.ceil(ctx.height / cellH);
  // Guard against a pathological cell size producing millions of glyphs.
  if (cols * rows > 600000) {
    state.built = true;
    return state;
  }

  for (let r = 0; r < rows; r++) {
    for (let c = 0; c < cols; c++) {
      const x = (c + 0.5) * cellW;
      const y = (r + 0.5) * cellH;

      let density: number;
      const signed = ctx.field.distance(x, y);

      if (v.mapping === 'distance') {
        density = 1 - clamp01(Math.abs(signed) / Math.max(1, v.distanceReach));
      } else {
        // Four sub-samples, enough to stabilise thin stems without costing much.
        const q = 0.25;
        density =
          (ctx.field.coverage(x - cellW * q, y - cellH * q) +
            ctx.field.coverage(x + cellW * q, y - cellH * q) +
            ctx.field.coverage(x - cellW * q, y + cellH * q) +
            ctx.field.coverage(x + cellW * q, y + cellH * q)) /
          4;
      }

      const isBackground = density < 0.02;
      if (isBackground && !v.background) continue;

      const set = isBackground ? bgChars : chars;
      const index = isBackground
        ? ctx.rng.int(0, set.length - 1)
        : Math.min(set.length - 1, Math.floor(density * set.length));

      state.cells.push({
        x,
        y,
        char: set[index] ?? ' ',
        density,
        distance: signed,
        size: cellW * v.fontSize * (1 + ctx.rng.signed(v.sizeJitter)),
        rotation: ctx.rng.signed(v.rotationJitter * DEG),
        tint: ctx.rng.next(),
        isBackground,
      });
    }
  }

  state.built = true;
  return state;
}

export function isComplete(state: AsciiMosaicState): boolean {
  return state.built;
}

export function draw(state: AsciiMosaicState, target: RenderTarget, ctx: TechniqueContext): void {
  const v = values(ctx);
  const c = target.ctx;
  const reach = Math.max(1, ctx.field.maxOutside);

  c.textAlign = 'center';
  c.textBaseline = 'middle';

  // Group by colour and size so the number of state changes stays proportional to the
  // palette, not to the cell count.
  let lastFont = '';
  for (const cell of state.cells) {
    if (cell.char === ' ') continue;

    let t: number;
    switch (v.colorMode) {
      case 'density':
        t = clamp01(cell.density);
        break;
      case 'distance':
        t = clamp01(Math.abs(cell.distance) / reach);
        break;
      case 'random':
        t = cell.tint;
        break;
      default:
        t = 0;
    }
    const alpha = (cell.isBackground ? v.backgroundOpacity : 1) * v.opacity;
    c.fillStyle = v.colorMode === 'solid' ? state.palette.stop(0, alpha) : state.palette.at(t, alpha);

    const font = `${cell.size.toFixed(1)}px ui-monospace, "SF Mono", Menlo, monospace`;
    if (font !== lastFont) {
      c.font = font;
      lastFont = font;
    }

    if (cell.rotation !== 0) {
      c.save();
      c.translate(cell.x, cell.y);
      c.rotate(cell.rotation);
      c.fillText(cell.char, 0, 0);
      c.restore();
    } else {
      c.fillText(cell.char, cell.x, cell.y);
    }
  }
}

export function elementCount(state: AsciiMosaicState): number {
  return state.cells.length;
}

export function toSVG(state: AsciiMosaicState, ctx: TechniqueContext): string {
  const v = values(ctx);
  const reach = Math.max(1, ctx.field.maxOutside);
  const escape = (s: string): string => s.replace(/[<>&]/g, (ch) => ({ '<': '&lt;', '>': '&gt;', '&': '&amp;' })[ch] ?? ch);
  const f = (n: number): string => n.toFixed(1).replace(/\.?0+$/, '');

  const parts: string[] = [
    `<g text-anchor="middle" dominant-baseline="central" font-family="ui-monospace, monospace">`,
  ];
  for (const cell of state.cells) {
    if (cell.char === ' ') continue;
    let t: number;
    switch (v.colorMode) {
      case 'density':
        t = clamp01(cell.density);
        break;
      case 'distance':
        t = clamp01(Math.abs(cell.distance) / reach);
        break;
      case 'random':
        t = cell.tint;
        break;
      default:
        t = 0;
    }
    const alpha = (cell.isBackground ? v.backgroundOpacity : 1) * v.opacity;
    const color = v.colorMode === 'solid' ? state.palette.stop(0) : state.palette.at(t);
    const transform = cell.rotation !== 0 ? ` transform="rotate(${(cell.rotation / DEG).toFixed(1)} ${f(cell.x)} ${f(cell.y)})"` : '';
    parts.push(
      `<text x="${f(cell.x)}" y="${f(cell.y)}" font-size="${f(cell.size)}" fill="${color}" fill-opacity="${alpha.toFixed(3)}"${transform}>${escape(cell.char)}</text>`,
    );
  }
  parts.push('</g>');
  return parts.join('\n');
}
